import React from "react";
import { Box, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import "./BetsEmptyState.scss";

/**
 * - `no-bets`    — the user has placed no bets at all.
 * - `no-results` — the user has bets, but the active filters exclude them all.
 */
export type BetsEmptyVariant = "no-bets" | "no-results";

interface BetsEmptyStateProps {
  /** Which empty case to show. Defaults to `no-bets`. */
  variant?: BetsEmptyVariant;
  /** Slightly larger sizing for the desktop (Browser) tree. */
  isDesktop?: boolean;
  /** Override the variant's default copy / icon. */
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
}

const VARIANTS: Record<
  BetsEmptyVariant,
  { title: string; subtitle: string; icon: React.ReactNode }
> = {
  "no-bets": {
    title: "No valid bets to display",
    subtitle:
      "Once you predict the score of an upcoming match, your bets will appear here.",
    icon: <ReceiptLongOutlinedIcon className="betsEmptyState__icon" />,
  },
  "no-results": {
    title: "No matching bets",
    subtitle:
      "No bets match your current filters. Try adjusting or clearing them to see more.",
    icon: <SearchOffOutlinedIcon className="betsEmptyState__icon" />,
  },
};

/**
 * Elegant empty-state shown when a user's bet list has nothing to display.
 * Shared by the mobile (MobileBetsBody) and desktop (BrowserBetsBody) trees so
 * both render one identical, on-brand placeholder instead of drifting markup.
 */
const BetsEmptyState: React.FC<BetsEmptyStateProps> = ({
  variant = "no-bets",
  isDesktop = false,
  title,
  subtitle,
  icon,
}) => {
  const preset = VARIANTS[variant];
  return (
    <Box
      className={`betsEmptyState${isDesktop ? " betsEmptyState--desktop" : ""}`}
      role="status"
    >
      <span className="betsEmptyState__badge" aria-hidden="true">
        {icon ?? preset.icon}
      </span>
      <Typography className="betsEmptyState__title">
        {title ?? preset.title}
      </Typography>
      <Typography className="betsEmptyState__subtitle">
        {subtitle ?? preset.subtitle}
      </Typography>
    </Box>
  );
};

export default BetsEmptyState;
